// apps/app/src/coachmark/content.ts
// Typed registry of every coachmark defined in v1. Adding a new hint is a
// one-file change: extend COACHMARK_IDS, append ALLOWED_TRIGGER_EVENTS if
// the trigger is novel, and add the entry. The build-time guard
// (apps/app/scripts/check-coachmarks.ts) verifies targetSelector resolves.

export const ALLOWED_TRIGGER_EVENTS = [
  'match.started',
  'unit.trained',
  'combat.resolved',
  'turn.ended',
  'match.ended',
] as const;

export type TriggerEvent = (typeof ALLOWED_TRIGGER_EVENTS)[number];

export type CoachmarkId = 'start-game' | 'first-scout' | 'first-combat' | 'end-turn' | 'match-end';

export interface CoachmarkEntry {
  readonly id: CoachmarkId;
  readonly title: string;
  readonly body: string;
  readonly targetSelector: string;
  readonly triggerEvent: TriggerEvent;
  readonly learnMoreUrl?: string;
  /**
   * `target` (default) floats the card beside its target. `bottom-right`
   * docks it in the bottom-right of the map area instead — for hints whose
   * target lives in the crowded top bar. The target must still exist.
   */
  readonly placement?: 'target' | 'bottom-right';
  /**
   * When this selector matches anything, the hint closes instead of showing.
   * For onboarding hints whose audience is defined by absence: the start-game
   * hint is a first-timer's hint, and the Continue button only exists for a
   * returning player.
   */
  readonly hideWhenSelector?: string;
}

export const coachmarkContent: Record<CoachmarkId, CoachmarkEntry> = {
  'start-game': {
    id: 'start-game',
    title: 'You are the blue empire',
    body: 'Train units, found cities, research doctrines. Your goal: complete a victory path before your rivals do.',
    targetSelector: "[data-coachmark-target='start-game']",
    triggerEvent: 'match.started',
    // A Continue button means a returning player: first-run onboarding is
    // noise for them, and at phone sizes the card physically covers the
    // menu's CTA stack — no placement avoids it (measured 320x568, 844x390;
    // audit exemption 2026-10-26, removed with this fix).
    hideWhenSelector: '[data-testid="continue-game"]',
  },
  'first-scout': {
    id: 'first-scout',
    title: 'Scout a tile to reveal fog',
    body: 'Click any owned unit, then click an unexplored tile to scout it. Hidden resources and enemy units wait under the fog.',
    targetSelector: "[data-coachmark-target='first-scout']",
    triggerEvent: 'unit.trained',
  },
  'first-combat': {
    id: 'first-combat',
    title: 'Attack to enter combat',
    body: 'You can only attack enemy units and cities. Click Attack after selecting one of your units, then pick a target.',
    targetSelector: "[data-coachmark-target='first-combat']",
    triggerEvent: 'combat.resolved',
  },
  'end-turn': {
    id: 'end-turn',
    title: 'Ticks advance on their own',
    body: 'The simulation runs continuously — the tick counter in the top bar advances on its own. Pause from the time controls if you want a moment to plan before the next tick.',
    targetSelector: "[data-coachmark-target='end-turn']",
    triggerEvent: 'turn.ended',
    placement: 'bottom-right',
  },
  'match-end': {
    id: 'match-end',
    title: 'Victory or defeat — review the summary',
    body: 'Use this screen to see your final score, biggest plays, and remaining unlocks. Click New Match to start fresh.',
    targetSelector: "[data-coachmark-target='match-end']",
    triggerEvent: 'match.ended',
  },
};

export function idsForTrigger(trigger: TriggerEvent): CoachmarkId[] {
  const out: CoachmarkId[] = [];
  for (const [id, entry] of Object.entries(coachmarkContent) as [CoachmarkId, CoachmarkEntry][]) {
    if (entry.triggerEvent === trigger) out.push(id);
  }
  return out;
}
